"use client";

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import PageHeaderWithStats from "@/components/common/PageHeaderWithStats";
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  HeartIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "react-hot-toast";
import { useBlogs, useLikeBlog } from "@/hooks/useBlogs";
import { Footer } from "@/components/Footer";

const BlogPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());

  // Use React Query for blogs
  const { data, isLoading, error } = useBlogs();
  const likeBlog = useLikeBlog();

  const blogs = useMemo(() => data?.blogs || [], [data]);



  // Load liked posts from localStorage on mount
  useEffect(() => {
    const savedLikes = localStorage.getItem("blog-likes");
    if (savedLikes) {
      setLikedPosts(new Set(JSON.parse(savedLikes)));
    }
  }, []);

  // Save liked posts to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("blog-likes", JSON.stringify(Array.from(likedPosts)));
  }, [likedPosts]);

  // Strip HTML tags for content searching
  const stripHtml = (html: string) =>
    html ? html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

  // Filter blogs based on search query
  const filteredBlogs = useMemo(() => {
    if (!searchQuery.trim()) return blogs;

    const q = searchQuery.toLowerCase().trim();

    return blogs.filter((blog) => {
      // Match title
      if (blog.title.toLowerCase().includes(q)) return true;
      // Match excerpt
      if (blog.excerpt && blog.excerpt.toLowerCase().includes(q)) return true;
      // Match tags
      if (
        blog.tags &&
        blog.tags.some((tag) => tag.toLowerCase().includes(q))
      )
        return true;
      // Match category
      if (blog.category && blog.category.toLowerCase().includes(q))
        return true;
      // Match author name
      if (blog.author?.name?.toLowerCase().includes(q)) return true;
      // Match stripped content (HTML-safe)
      if (blog.content && stripHtml(blog.content).toLowerCase().includes(q))
        return true;

      return false;
    });
  }, [searchQuery, blogs]);

  const handleLike = async (postId: string, slug: string) => {
    if (likeBlog.isPending) return; // Prevent multiple clicks

    const wasLiked = likedPosts.has(postId);
    const newLikedPosts = new Set(likedPosts);

    if (wasLiked) {
      // Just remove from local liked state, don't call API for unlike
      newLikedPosts.delete(postId);
      setLikedPosts(newLikedPosts);
    } else {
      // Add to local liked state and call API
      newLikedPosts.add(postId);
      setLikedPosts(newLikedPosts);

      likeBlog.mutate({ slug, blogId: postId });
    }
  };

  const formatDate = (date: Date | string) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px] flex items-center justify-center zoom-container">
        <LoadingSpinner
          fullScreen={false}
          showText={true}
          text="Loading blog posts..."
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px] flex items-center justify-center zoom-container">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error Loading Blogs
          </h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-[#ff7043] text-white rounded-lg hover:bg-[#ff7043]/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px]">
      {/* Header and Stats Section */}
      <PageHeaderWithStats
        badge={{ text: "Wellness Blog", showPulse: true }}
        title={{ main: "Wellness", highlighted: "Insights" }}
        description="Explore expert tips, mindful practices, and inspiring stories to enhance your wellness journey"
        stats={[
          {
            icon: DocumentTextIcon,
            value: filteredBlogs.length,
            label: "Articles",
          },
          {
            icon: EyeIcon,
            value: filteredBlogs.reduce(
              (acc, blog) => acc + (blog.views || 0),
              0
            ),
            label: "Total Views",
            formatter: (val) =>
              typeof val === "number" ? val.toLocaleString() : val.toString(),
          },
          {
            icon: HeartIcon,
            value: filteredBlogs.reduce((acc, blog) => acc + blog.likes, 0),
            label: "Total Likes",
          }
        ]}
        loading={isLoading}
      />

      {/* Search Section - Separate container like classes page */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-6 sm:mb-8 zoom-container">
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for wellness insights, tips, or inspiration..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 sm:px-6 py-3 sm:py-4 pl-12 sm:pl-14 pr-12 sm:pr-16 rounded-xl border-2 border-gray-200 bg-white text-gray-700 placeholder-gray-500 focus:border-[#ff7043] focus:outline-none focus:ring-2 focus:ring-[#ff7043]/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
            />
            <MagnifyingGlassIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2" />
            <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-[#ff7043] hover:bg-[#ff7043]/90 text-white p-2 rounded-lg transition-colors duration-300 group">
              <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Blog Grid - Separate container like classes page */}
      <div className="container mx-auto px-4 sm:px-6 zoom-container">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 sm:justify-items-center">
          {filteredBlogs.map((blog) => (
            <article
              key={blog._id}
              className="group bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 w-full flex flex-row sm:flex-col"
            >
              {/* Blog Image with Overlay - Matching Class Card Style */}
              <div className="relative w-1/3 sm:w-full min-h-[180px] sm:min-h-0">
                <div className="relative h-full sm:h-[200px] md:h-[240px] w-full">
                  {blog.featuredImage ? (
                    <Image
                      src={blog.featuredImage}
                      alt={blog.title}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <DocumentTextIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
                </div>

                {/* Tags and Category Badges - Show only first tag on mobile */}
                <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex gap-1 sm:gap-2">
                  {blog.tags.slice(0, 1).map((tag) => (
                    <span
                      key={tag}
                      className="px-2 sm:px-3 py-1 bg-[#ff7043]/90 backdrop-blur-sm rounded-full text-[10px] sm:text-xs font-medium text-white border border-[#ff7043]/30"
                    >
                      {tag}
                    </span>
                  ))}
                  {blog.tags.length > 1 && (
                    <span className="hidden sm:inline-block px-2 sm:px-3 py-1 bg-[#ff7043]/90 backdrop-blur-sm rounded-full text-xs font-medium text-white border border-[#ff7043]/30">
                      {blog.tags[1]}
                    </span>
                  )}
                  {blog.tags.length > 2 && (
                    <span className="hidden sm:inline-block px-2 sm:px-3 py-1 bg-[#463a33]/90 backdrop-blur-sm rounded-full text-xs font-medium text-white border border-white/30">
                      +{blog.tags.length - 2}
                    </span>
                  )}
                </div>

                {/* Action Buttons - Show on mobile */}
                <div className="absolute top-2 sm:top-3 right-2 sm:right-3 flex gap-2">
                  <button
                    onClick={() => handleLike(blog._id, blog.slug)}
                    disabled={likeBlog.isPending}
                    className={`p-1.5 sm:p-2 rounded-full backdrop-blur-sm transition-all duration-300 ${likedPosts.has(blog._id)
                      ? "bg-[#ff7043] text-white"
                      : "bg-white/20 text-white hover:bg-[#ff7043]/20"
                      } ${likeBlog.isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {likeBlog.isPending ? (
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : likedPosts.has(blog._id) ? (
                      <HeartIconSolid className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </button>
                </div>

                {/* Author Information Overlay - Show on larger screens only */}
                <div className="absolute bottom-0 left-0 w-full p-3 sm:p-4 text-white hidden sm:block">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xs sm:text-sm font-bold">
                        {blog.author.name}
                      </h3>
                      <p className="text-xs opacity-90">
                        Author • {blog.views || 0} views
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Blog Information - Matching Class Card Style */}
              <div className="p-2 sm:p-3 md:p-4 w-2/3 sm:w-full flex flex-col">
                {/* Author info on mobile only */}
                <div className="flex items-center gap-1.5 mb-1.5 sm:hidden">
                  <span className="text-[9px] font-medium text-[#463a33]">
                    {blog.author.name}
                  </span>
                </div>

                {/* Quick Stats - Reduced on mobile */}
                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-1.5 sm:mb-2 md:mb-3 text-[9px] sm:text-xs md:text-sm text-[#463a33]/70">
                  <div className="flex items-center gap-1">
                    <HeartIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-[#ff7043]" />
                    <span>{blog.likes}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <EyeIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-[#463a33]" />
                    <span>{blog.views || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CalendarIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-[#463a33]" />
                    <span className="truncate">
                      {blog.publishedAt
                        ? formatDate(blog.publishedAt)
                        : formatDate(blog.createdAt)}
                    </span>
                  </div>
                </div>

                <h2 className="text-base font-semibold text-[#463a33] mb-1 sm:mb-1.5 md:mb-2 line-clamp-2">
                  {blog.title}
                </h2>

                {/* Description */}
                <p className="text-sm text-[#463a33]/80 mb-2 sm:mb-2.5 md:mb-3 line-clamp-2 sm:line-clamp-3">
                  {blog.excerpt ||
                    (blog.content
                      ? blog.content.replace(/<[^>]*>?/gm, "").substring(0, 150) + "..."
                      : "Discover insights and wisdom in this thoughtful piece...")}
                </p>

                {/* Additional Tags - Show on larger screens */}
                <div className="hidden sm:flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
                  <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full border border-[#ff7043] text-xs text-[#ff7043] bg-[#ff7043]/5">
                    <DocumentTextIcon className="w-3 h-3 mr-1" />
                    Blog Post
                  </span>

                  {blog.tags.slice(2, 4).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full border border-[#463a33] text-xs text-[#463a33] bg-[#463a33]/5"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Call-to-Action Buttons - At bottom */}
                <div className="flex gap-2 mt-auto">
                  <Link
                    href={`/blogs/${blog.slug}`}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#ff7043] text-white text-xs py-2.5 px-4 rounded-lg hover:bg-[#ff7043]/90 transition-all duration-200 transform hover:scale-[1.02]"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    <span>Read</span>
                  </Link>
                  <button
                    onClick={() => {
                      navigator
                        .share?.({
                          title: blog.title,
                          text: blog.content?.substring(0, 100) || "",
                          url: `/blogs/${blog.slug}`,
                        })
                        .catch(() => {
                          navigator.clipboard.writeText(
                            `${window.location.origin}/blogs/${blog.slug}`
                          );
                          toast.success("Link copied to clipboard!");
                        });
                    }}
                    className="flex-1 flex items-center justify-center gap-2 border border-[#463a33] text-[#463a33] text-xs py-2.5 px-4 rounded-lg hover:bg-[#463a33]/5 transition-all duration-200"
                  >
                    <ShareIcon className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Empty State - Matching Class Page Style */}
        {filteredBlogs.length === 0 && (
          <div className="text-center my-12 p-8 bg-white/95 backdrop-blur-sm rounded-xl border border-gray-200">
            <div className="text-6xl mb-4">{searchQuery ? "🔍" : "✍️"}</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {searchQuery ? "No articles found" : "Coming Soon!"}
            </h3>
            <p className="text-gray-600 mb-4">
              {searchQuery
                ? "We couldn't find any articles matching your search criteria."
                : "We're crafting amazing wellness content for you. Our first articles will be available soon!"}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* End of List Message - Show when there are blogs */}
        {filteredBlogs.length > 0 && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              You&apos;ve reached the end!
            </h3>
            <p className="text-gray-600 mb-4">
              No more articles to load.{" "}
              {searchQuery
                ? "Try adjusting your search to see more results."
                : "Check back soon for new wellness insights!"}
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogPage;
